import {AnalyticsEventActions} from '@mintdata-wsp/common/controllers/analytics/AnalyticsEventActions';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {Organization} from '@mintdata-wsp/common/state/DataState';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {IPageSnapshot, Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {
    MouseOverRef,
    SelectionInteractionMode,
    SurfaceBounds,
    SurfacePoint
} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {BoxReparentingDisabledOn} from '@mintdata-wsp/wsp/controllers/visualDesigner/workbench/surface/SurfaceBoxReparentingController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Scrollable, ScrollAppearance, ScrollBehavior} from '@mintdata-wsp/wsp/ui/components/scrollable/Scrollable';
import {SurfaceAreaContextMenu} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/contextMenu/SurfaceAreaContextMenu';
import {ResizeHandles} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/ResizeHandles';
import {SurfaceDrawingComponentFrame} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/SurfaceDrawingComponentFrame';
import {SurfaceGuidelines} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/SurfaceGuidelines';
import {SurfaceKeyboardShortcuts} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/SurfaceKeyboardShortcuts';
import {SurfacePage} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/SurfacePage';
import {SurfaceSelectionFrame} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/SurfaceSelectionFrame';
import {SurfaceStubPage} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/SurfaceStubPage';
import {Bounds} from '@mintdata/mint-kit/api/models/Bounds';
import {Point} from '@mintdata/mint-kit/api/models/Point';
import {DomUtils} from '@mintdata/mint-kit/impl/util/DomUtils';
import * as classnames from 'classnames/bind';
import {action, computed} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {CSSProperties} from 'react';
import {DraggableCore, DraggableData} from 'react-draggable';
import Dropzone from 'react-dropzone';
import {ActiveUserPointers} from './ActiveUserPointers';

const styles = require('./SurfaceArea.less');
const cx = classnames.bind(styles);

@observer
export class SurfaceArea extends React.Component<any, any>
{
    private _refHandlers = {
        surface: (ref:HTMLElement | null) =>
        {
            if( !ref ) return;

            const {area} = rootState.ui.visualDesigner.surfaceEditor;
            area.surfaceAreaDomElement = ref;
        }
    };

    public componentDidMount()
    {
        const designState = controllers.ui.openDesign.getDesignState();

        if( designState && designState.surfaceSnapshot )
            this._sendMDAEventFirstRender();
    }

    public render():JSX.Element
    {
        const {zoom} = rootState.ui.visualDesigner.surfaceEditor.area;
        const {surfaceArea, surfaceSelection, surfaceDrawingComponent, surfaceBoxReparenting} = controllers.ui;

        const isPreviewMode = surfaceArea.isInPreviewOrZenPreviewMode();
        const isZenPreviewMode = surfaceArea.isInZenPreviewMode();
        const panPosition = surfaceArea.getPanPosition();
        const isPanEnabled = surfaceArea.isPanEnabled();
        const isBoxReparentingEnabled = surfaceBoxReparenting.isBoxReparentingEnabled();
        const isCursorSelectionEnabled = surfaceSelection.isCursorSelectionEnabled();
        const isDrawingComponentEnabled = surfaceDrawingComponent.isDrawingComponentEnabled();

        const surfaceAreaStyle:CSSProperties = isZenPreviewMode ? {
            width: '100%',
            height: '100%'
        } : {
            transformOrigin: 'left top',
            transform: `scale(${zoom})`,
            minWidth: `calc(${100 / zoom}%)`,
            minHeight: `calc(${100 / zoom}%)`
        };

        const panElementStyle:CSSProperties = isZenPreviewMode ? {
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%'
        } : {
            position: 'absolute',
            transform: `translate3d(${panPosition.x}px, ${panPosition.y}px, 0)`
        };

        return this._wrapIntoDropzone(
            <SurfaceKeyboardShortcuts>
                <SurfaceAreaContextMenu>
                    <Scrollable
                        isScrollX={false}
                        isScrollY={false}
                        behavior={ScrollBehavior.OnHover}
                        appearance={ScrollAppearance.ForLightBlueContent}
                    >
                        <DraggableCore
                            onStart={this._handleSurfaceDragStart}
                            onDrag={this._handleSurfaceDrag}
                            onStop={this._handleSurfaceDragStop}
                            enableUserSelectHack={false}
                            disabled={isPreviewMode}
                        >
                            <div
                                className={cx('surfaceArea', {
                                    isPanEnabled,
                                    isPreviewMode,
                                    isCursorSelectionEnabled,
                                    isDrawingComponentEnabled,
                                    isBoxReparentingEnabled
                                })}
                                id={'surfaceArea'}
                                ref={this._refHandlers.surface}
                                tabIndex={-1}
                                onMouseDownCapture={this._handleSurfaceMouseDown}
                                onMouseUpCapture={this._handleSurfaceMouseUp}
                                {...this._dndBoxStyles}
                                {...this._scrollbarStyles}
                                style={surfaceAreaStyle}
                                onFocus={this._handleFocus}
                                onPaste={this._handlePaste}
                                onMouseMove={this._handleSurfaceMouseMove}
                            >
                                <div
                                    style={panElementStyle}
                                >
                                    {this._renderPages()}
                                    <SurfaceGuidelines />
                                    {!isPreviewMode && <SurfaceSelectionFrame />}
                                    {!isPreviewMode && <SurfaceDrawingComponentFrame />}
                                </div>
                            </div>
                        </DraggableCore>
                        <ActiveUserPointers surface />
                    </Scrollable>
                </SurfaceAreaContextMenu>
            </SurfaceKeyboardShortcuts>
        );
    }

    private _renderPages():Array<JSX.Element>
    {
        const designState = controllers.ui.openDesign.getDesignState();

        if( !designState || !(designState.surface || designState.surfaceSnapshot) )
            return null;

        if( designState.surfaceSnapshot )
        {
            const {pages, startPageId} = designState.surfaceSnapshot;

            return Object.values(pages)
                .map((page:IPageSnapshot) => (
                    <SurfaceStubPage key={page.id} page={page} isActive={startPageId === page.id} />
                ));
        }

        const {surfaceArea} = controllers.ui;
        const {pages} = designState.surface;
        const activePage:Page = surfaceArea.getActivePage();

        const allowAnimation:boolean = controllers.ui.surfaceArea.getState().allowPagesAnimation;

        return Array.from(pages.values())
            .map((page:Page) =>
            {
                const isActive:boolean = activePage ? activePage.id === page.id : false;
                const overlayIndex:number = isActive ? -1 : commonControllers.designState.getIndexInOverlaysByPage(page);
                const isOverlay:boolean = overlayIndex !== -1;
                const isHidden:boolean = isOverlay && activePage.overlays[overlayIndex].isHidden;

                return (
                    <SurfacePage
                        key={page.id}
                        page={page}
                        isActive={isActive}
                        isOverlay={isOverlay}
                        isHidden={isHidden}
                        stackIndex={overlayIndex}
                        allowAnimation={allowAnimation}
                    />
                );
            });
    }

    private _wrapIntoDropzone(element:JSX.Element):JSX.Element
    {
        const {surfaceFileUpload, surfaceArea} = controllers.ui;

        return (
            <Dropzone
                accept={'image/*'}
                multiple={true}
                onDragEnter={() => surfaceFileUpload.setIsFileDraggingOverSurface(true)}
                onDragLeave={() => surfaceFileUpload.setIsFileDraggingOverSurface(false)}
                onDrop={(a, r, e) => surfaceFileUpload.handleDroppedOnSurfaceFiles(e, a, r)}
                disabled={surfaceArea.isInPreviewOrZenPreviewMode()}
            >
                {({getRootProps, getInputProps}) => (
                    <div
                        {...getRootProps()}
                        className={cx('fileDropzone')}
                    >
                        {element}
                    </div>
                )}
            </Dropzone>
        );
    }

    @action
    private _handleSurfaceMouseDown = (e:React.MouseEvent<any>) =>
    {
        const {surfaceArea, surfaceMoving, surfaceSelection} = controllers.ui;

        if( e.buttons === 4 )
        {
            surfaceArea.enablePan();
            const {x, y} = surfaceArea.getPanPosition();
            surfaceMoving.setDrag(x, y, 0, 0);
        }

        if( surfaceArea.isPanEnabled() )
            return;

        if( this._isSurfaceArea(e) )
            surfaceSelection.clearSelectedBoxesForPage();
        else
        {
            if( !surfaceArea.isInPreviewOrZenPreviewMode() )
            {
                const mouseOverRef:MouseOverRef = surfaceArea.getMouseOverRef();

                if( mouseOverRef && !this._isResizeHandle(e) )
                    surfaceArea.changeSelectedBoxesOnBoxClick(e.shiftKey, false, mouseOverRef.box);
            }
        }
    };

    @action
    private _handleSurfaceMouseUp = (e:React.MouseEvent<any>) =>
    {
        if( e.button === 1 )
            controllers.ui.surfaceArea.disablePan();
    }

    @action
    private _handleSurfaceDragStart = (e:MouseEvent, data:DraggableData):void =>
    {
        const {surfaceArea, surfaceMoving, surfaceSelection, surfaceDrawingComponent} = controllers.ui;

        if( surfaceArea.isPanEnabled() )
        {
            const panPosition = surfaceArea.getPanPosition();

            surfaceMoving.setDrag(panPosition.x, panPosition.y, 0, 0);
        }
        else if( this._isSurfaceArea(e) )
        {
            // todo: remove this `if` when the layoutPropsValues availability of a not resolved box issue is solved
            if( surfaceArea.areBoxesResolved(surfaceArea.getAllBoxes(surfaceArea.getCurrentLayerPage())) )
            {
                const panPosition = surfaceArea.getPanPosition();
                const {area: {zoom}} = rootState.ui.visualDesigner.surfaceEditor;

                const dragStartX = Math.round(data.x / zoom) - panPosition.x;
                const dragStartY = Math.round(data.y / zoom) - panPosition.y;
                const dragDeltaX = 0;
                const dragDeltaY = 0;

                const bounds = Bounds.from(dragStartX, dragStartY, dragDeltaX, dragDeltaY);

                if( surfaceDrawingComponent.isDrawingComponentEnabled() )
                {
                    surfaceDrawingComponent.setDrawingComponentBounds(bounds);
                    surfaceDrawingComponent.setDrawingStartedPoint(SurfacePoint.from(dragStartX, dragStartY));
                }
                else
                {
                    surfaceSelection.clearSelectedBoxesForPage();
                    surfaceSelection.enableCursorSelection();
                    surfaceSelection.setSelectionBounds(bounds);
                }

                surfaceMoving.setDrag(dragStartX, dragStartY, dragDeltaX, dragDeltaY);
            }
        }
        else
            this._handleBoxDragStart(e, data);
    };

    @action
    private _handleSurfaceDrag = (e:MouseEvent, data:DraggableData):void =>
    {
        const {surfaceArea, surfaceMoving, surfaceSelection, surfaceDrawingComponent} = controllers.ui;

        let {dragStartX, dragStartY, dragDeltaX, dragDeltaY} = surfaceMoving.getDrag();

        const isPanEnabled = surfaceArea.isPanEnabled();

        if( isPanEnabled )
        {
            const {area: {zoom}} = rootState.ui.visualDesigner.surfaceEditor;
            e.stopPropagation();

            dragDeltaX += data.deltaX;
            dragDeltaY += data.deltaY;

            surfaceArea.setPanPosition(Point.from(
                dragStartX + Math.round(dragDeltaX / zoom),
                dragStartY + Math.round(dragDeltaY / zoom)
            ));

            surfaceMoving.setDrag(null, null, dragDeltaX, dragDeltaY);
        }
        else if( surfaceSelection.isCursorSelectionEnabled() && surfaceDrawingComponent.isDrawingComponentEnabled() )
        {
            return;
        }
        else if( surfaceSelection.isCursorSelectionEnabled() || surfaceDrawingComponent.isDrawingComponentEnabled() )
        {
            const {area: {zoom}} = rootState.ui.visualDesigner.surfaceEditor;

            dragDeltaX += data.deltaX;
            dragDeltaY += data.deltaY;

            const newSelectionBounds = Bounds.from(
                dragStartX,
                dragStartY,
                Math.round(dragDeltaX / zoom),
                Math.round(dragDeltaY / zoom)
            );

            surfaceSelection.isCursorSelectionEnabled()
                ? surfaceSelection.setSelectionBounds(newSelectionBounds)
                : surfaceDrawingComponent.setDrawingComponentBounds(newSelectionBounds);

            surfaceMoving.setDrag(null, null, dragDeltaX, dragDeltaY);
        }
        else
            this._handleBoxDragInProgress(e, data);
    };

    @action
    private _handleSurfaceDragStop = (e:MouseEvent, data:DraggableData):void =>
    {
        const {surfaceArea, surfaceSelection, surfaceDrawingComponent} = controllers.ui;

        if( surfaceArea.isPanEnabled() )
            return;

        if( surfaceDrawingComponent.isDrawingComponentEnabled() )
        {
            if( !surfaceSelection.isCursorSelectionEnabled() )
            {
                const bounds:Bounds = surfaceDrawingComponent.getDrawingComponentBounds(true);

                if( bounds && (bounds.width !== 0 || bounds.height !== 0) )
                    surfaceDrawingComponent.drawComponent();
            }

            surfaceSelection.disableCursorSelection();
        }
        else if( surfaceSelection.isCursorSelectionEnabled() )
        {
            const selectionBounds = surfaceSelection.getSelectionBounds(true);

            const boxesToSelect = surfaceArea.findBoxesInBounds(selectionBounds)
                .filter((box:Box) => !box.componentDesc.lockProp.value.value);

            surfaceSelection.setMultipleBoxSelection(boxesToSelect);

            surfaceSelection.disableCursorSelection();
        }
        else
            this._handleBoxDragStop(e, data);
    };

    @action
    private _handleBoxDragStart = (e:MouseEvent, data:DraggableData):void | false =>
    {
        const {surfaceArea, surfaceSelection, surfaceMoving, surfaceBoxReparenting, undoRedo} = controllers.ui;
        const selectedBoxes = controllers.ui.surfaceSelection.getSelectedBoxes();

        if(
            surfaceArea.isInPreviewOrZenPreviewMode() ||
            this._isResizeHandle(e) ||
            !selectedBoxes.length ||
            !surfaceArea.areBoxesResolved(selectedBoxes)
        )
            return false;

        for( const selectedBox of selectedBoxes )
            if( surfaceArea.isBoxOnMoveByAnotherUser(selectedBox)
             || surfaceArea.isBoxParentOnMoveByAnotherUser(selectedBox)
             || surfaceArea.isBoxChildOnMoveByAnotherUser(selectedBox) )
                return false;
            
        undoRedo.disableRecording();

        surfaceSelection.setSelectionInteractionMode(SelectionInteractionMode.StartMoving);

        if( e.altKey )
        {
            surfaceArea.setMouseOverRef(null);
            surfaceArea.cloneSelectedBoxes();
        }

        if( e.metaKey )
            surfaceBoxReparenting.enableBoxReparenting();

        if( !surfaceBoxReparenting.isBoxReparentingEnabled() )
        {
            const bounds = surfaceArea.getBoxesBounds(selectedBoxes);

            surfaceSelection.setSelectionBounds(bounds);

            surfaceMoving.setBoxDrag(bounds.x, bounds.y, 0, 0);
        }
    };

    @action
    private _handleBoxDragInProgress = (e:MouseEvent, data:DraggableData):void =>
    {
        const {surfaceArea, surfaceSelection, surfaceGuidelines} = controllers.ui;
        const selectedBoxes = controllers.ui.surfaceSelection.getSelectedBoxes();

        for( const selectedBox of selectedBoxes )
            if( surfaceArea.isBoxOnMoveByAnotherUser(selectedBox)
             || surfaceArea.isBoxParentOnMoveByAnotherUser(selectedBox)
             || surfaceArea.isBoxChildOnMoveByAnotherUser(selectedBox) )
                return;

        const currentMode = surfaceSelection.getSelectionInteractionMode();

        if( currentMode !== SelectionInteractionMode.Moving )
            surfaceSelection.setSelectionInteractionMode(SelectionInteractionMode.Moving);

        surfaceGuidelines.throttledUpdateGuidelines();

        surfaceArea.dragSelectedBoxesBy(data.deltaX, data.deltaY);
    };

    @action
    private _handleBoxDragStop = (e:any, data:DraggableData):void =>
    {
        const {surfaceSelection, surfaceGuidelines, undoRedo, surfaceBoxReparenting, surfaceArea} = controllers.ui;

        undoRedo.enableRecording();

        surfaceSelection.setSelectionInteractionMode(SelectionInteractionMode.None);

        surfaceGuidelines.stopAndClear();

        if( surfaceBoxReparenting.isBoxReparentingEnabled() )
            surfaceBoxReparenting.disableBoxReparenting(BoxReparentingDisabledOn.Drop);
        
        surfaceArea.onBoxDragStopEvent();
    };

    @action.bound
    private _handleFocus(event:React.FocusEvent<HTMLDivElement>):void
    {
        const {surfaceArea} = controllers.ui;

        if( surfaceArea.isInPreviewOrZenPreviewMode() )
            return;

        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        if( area.surfaceAreaDomElement )
        {
            area.surfaceAreaDomElement.focus();
            DomUtils.setNodeSelection(area.surfaceAreaDomElement);
        }
    }

    @action.bound
    private _handlePaste(event:React.ClipboardEvent<HTMLDivElement>):void
    {
        const {surfaceFileUpload} = controllers.ui;

        surfaceFileUpload.handlePastingFromClipboardFiles(event);
    }

    private _isSurfaceArea(e:React.MouseEvent<any> | MouseEvent):boolean
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;
        const surfaceAreaDomElement = area.surfaceAreaDomElement;

        return e.target === surfaceAreaDomElement;
    }

    @action.bound
    private _handleSurfaceMouseMove(event:React.MouseEvent<HTMLDivElement>):void
    {
        const {surfaceArea, surfaceMoving} = controllers.ui;

        if( event.buttons === 4 && surfaceArea.isPanEnabled() )
        {
            const {area: {zoom}} = rootState.ui.visualDesigner.surfaceEditor;
            event.stopPropagation();

            let {dragStartX, dragStartY, dragDeltaX, dragDeltaY} = surfaceMoving.getDrag();

            dragDeltaX += event.nativeEvent.movementX;
            dragDeltaY += event.nativeEvent.movementY;

            surfaceArea.setPanPosition(Point.from(
                dragStartX + Math.round(dragDeltaX / zoom),
                dragStartY + Math.round(dragDeltaY / zoom)
            ));

            surfaceMoving.setDrag(null, null, dragDeltaX, dragDeltaY);
        }

        surfaceArea.onMouseMoveEvent(event.clientX, event.clientY);
    }

    private _isResizeHandle(event:any):boolean
    {
        const e = event.nativeEvent;
        const target = e.target;

        return ResizeHandles.isResizerHandle(target as HTMLElement);
    }

    @computed
    private get _dndBoxStyles():CSSProperties
    {
        const {surfaceDnD} = commonControllers;

        return surfaceDnD.getDropTargetStyles();
    }

    @computed
    private get _scrollbarStyles():CSSProperties
    {
        const {surfaceArea} = commonControllers;

        return surfaceArea.getScrollbarStyles();
    }

    private _sendMDAEventFirstRender()
    {
        const {userSession} = rootState;

        // for samples organization on website docs
        let currentOrganization:Organization = null;
        if( userSession )
        {
            currentOrganization = userSession.currentOrganization;
        }

        const {projectId, designId} = controllers.ui.openDesign.getOpenDesign().designRef;

        controllers.analytics.main.trackEvent(
            AnalyticsEventActions.WSP_DESIGNER_FIRST_RENDER,
            {
                is_from_wsp: true,

                org_sid: currentOrganization ? currentOrganization.siteId : '',
                org_name: currentOrganization ? currentOrganization.name : '',

                space_id: projectId,
                // space_name: currentProject.name,

                design_id: designId
                // design_name: design.name,
            }
        );
    }
}
